import React, { useState, useRef } from 'react';
import { Card, ProgressBar, ListGroup, Badge, Row, Col, Overlay, Popover } from 'react-bootstrap';
import { FaCheckCircle, FaTimesCircle, FaInfoCircle } from 'react-icons/fa';
import PropTypes from 'prop-types';

const getScoreVariant = (score) => {
  if (score >= 80) return 'success';
  if (score >= 60) return 'info';
  if (score >= 40) return 'warning';
  return 'danger';
};

/**
 * LenderMatchScore Component
 * 
 * Displays the match score for a lender in a compact format, including:
 * - Overall score with progress bar
 * - Matched criteria count (clickable/hoverable to show details)
 * - Missed criteria count (clickable/hoverable to show details)
 * 
 * @param {Object} props - Component props
 * @param {string} props.lenderId - ID of the lender
 * @param {string} props.lenderName - Name of the lender
 * @param {number} props.score - Match score (0-100)
 * @param {Array} props.matchedCriteria - Array of matched criteria objects
 * @param {Array} props.missedCriteria - Array of missed criteria objects
 * @param {boolean} props.expanded - Whether the details are expanded
 * @param {Function} props.onToggleExpand - Function to toggle expanded state
 * @returns {JSX.Element} LenderMatchScore component
 */
const LenderMatchScore = ({ 
  lenderId, 
  lenderName, 
  score, 
  matchedCriteria = [], 
  missedCriteria = [],
  expanded = false,
  onToggleExpand
}) => {
  const scoreVariant = getScoreVariant(score);
  
  // References for popover targets
  const matchedRef = useRef(null);
  const missedRef = useRef(null);
  
  // Local state for hover popovers
  const [showMatchedPopover, setShowMatchedPopover] = useState(false);
  const [showMissedPopover, setShowMissedPopover] = useState(false);
  
  // Check if lender has any criteria defined
  const hasCriteria = matchedCriteria.length > 0 || missedCriteria.length > 0;
  
  // Render criteria details in a popover
  const renderCriteriaPopover = (criteria, isMatched) => {
    const variant = isMatched ? 'success' : 'danger';
    const title = isMatched ? 'Matched Criteria' : 'Missed Criteria';
    const icon = isMatched ? <FaCheckCircle className="me-1" size="0.8em" /> : <FaTimesCircle className="me-1" size="0.8em" />;
    
    return (
      <Popover id={`popover-${isMatched ? 'matched' : 'missed'}-${lenderId}`} className="criteria-popover">
        <Popover.Header as="h6" className={`bg-${variant} text-white d-flex align-items-center py-1 px-2`}>
          {icon} {title}
        </Popover.Header>
        <Popover.Body className="p-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
          {criteria.length > 0 ? (
            <ListGroup variant="flush">
              {criteria.map((criterion, index) => (
                <ListGroup.Item key={index} className="py-1 px-2 d-flex justify-content-between align-items-center">
                  <div>
                    <span className="fw-medium small">{criterion.fieldName}</span>
                    <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                      {criterion.operator} {criterion.value}
                    </div>
                  </div>
                  <Badge 
                    bg={criterion.required ? 'danger' : 'secondary'} 
                    className="ms-1"
                    style={{ fontSize: '0.65rem' }}
                  >
                    {criterion.required ? 'Required' : `W: ${criterion.weight}`}
                  </Badge>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <div className="text-center text-muted p-2" style={{ fontSize: '0.8rem' }}>
              <FaInfoCircle className="me-1" size="0.8em" />
              No {isMatched ? 'matched' : 'missed'} criteria.
            </div>
          )}
        </Popover.Body>
      </Popover>
    );
  };
  
  return (
    <Card className="mb-2 shadow-sm border-0" style={{ transition: 'all 0.2s ease' }}>
      <Card.Body className="p-2">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <h6 className="mb-0 fs-6">{lenderName}</h6>
          {hasCriteria ? (
            <Badge 
              bg={scoreVariant} 
              style={{ fontSize: '0.95rem', padding: '0.25rem 0.5rem' }}
            >
              {score}%
            </Badge>
          ) : (
            <Badge 
              bg="secondary" 
              style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
            >
              No criteria
            </Badge>
          )}
        </div>
        
        <ProgressBar 
          variant={hasCriteria ? scoreVariant : "secondary"}
          now={hasCriteria ? score : 0}
          className="mb-1" 
          style={{ height: '6px', opacity: hasCriteria ? 1 : 0.5 }}
        />
        
        <Row className="text-center g-0 mb-0">
          <Col xs={6}>
            <div 
              className="d-flex align-items-center justify-content-center criteria-count"
              ref={matchedRef}
              onClick={() => {
                if (matchedCriteria.length > 0) {
                  setShowMatchedPopover(!showMatchedPopover);
                  setShowMissedPopover(false);
                }
              }}
              onMouseEnter={() => matchedCriteria.length > 0 && setShowMatchedPopover(true)}
              onMouseLeave={() => setShowMatchedPopover(false)}
              style={{ 
                cursor: matchedCriteria.length > 0 ? 'pointer' : 'default', 
                padding: '3px' 
              }}
            >
              <FaCheckCircle className="text-success me-1" size="0.8em" />
              <span className="fw-bold small">{matchedCriteria.length}</span>
              <span className="text-muted ms-1 small">matched</span>
            </div>
            
            <Overlay
              show={showMatchedPopover && matchedCriteria.length > 0}
              target={matchedRef.current}
              placement="bottom"
              container={document.body}
              rootClose
              onHide={() => setShowMatchedPopover(false)}
            >
              {renderCriteriaPopover(matchedCriteria, true)}
            </Overlay>
          </Col>
          
          <Col xs={6}>
            <div 
              className="d-flex align-items-center justify-content-center criteria-count"
              ref={missedRef}
              onClick={() => {
                if (missedCriteria.length > 0) {
                  setShowMissedPopover(!showMissedPopover);
                  setShowMatchedPopover(false);
                }
              }}
              onMouseEnter={() => missedCriteria.length > 0 && setShowMissedPopover(true)}
              onMouseLeave={() => setShowMissedPopover(false)}
              style={{ 
                cursor: missedCriteria.length > 0 ? 'pointer' : 'default', 
                padding: '3px' 
              }}
            >
              <FaTimesCircle className="text-danger me-1" size="0.8em" />
              <span className="fw-bold small">{missedCriteria.length}</span>
              <span className="text-muted ms-1 small">missed</span>
            </div>
            
            <Overlay
              show={showMissedPopover && missedCriteria.length > 0}
              target={missedRef.current}
              placement="bottom"
              container={document.body}
              rootClose
              onHide={() => setShowMissedPopover(false)}
            >
              {renderCriteriaPopover(missedCriteria, false)}
            </Overlay>
          </Col>
        </Row>
        
        {/* Show expanded details if specifically requested (not via hover) */}
        {expanded && (
          <div className="mt-2 pt-1 border-top">
            {matchedCriteria.length > 0 && (
              <div className="mb-2">
                <h6 className="text-success d-flex align-items-center small fw-bold mt-1">
                  <FaCheckCircle className="me-1" size="0.8em" /> Matched Criteria
                </h6>
                <ListGroup variant="flush" className="border-top border-bottom">
                  {matchedCriteria.map((criterion, index) => (
                    <ListGroup.Item key={index} className="py-1 px-2 d-flex justify-content-between align-items-center">
                      <div>
                        <span className="fw-medium small">{criterion.fieldName}</span>
                        <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                          {criterion.operator} {criterion.value}
                        </div>
                      </div>
                      <Badge 
                        bg={criterion.required ? 'danger' : 'secondary'} 
                        className="ms-1"
                        style={{ fontSize: '0.65rem' }}
                      >
                        {criterion.required ? 'Required' : `W: ${criterion.weight}`}
                      </Badge>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            )}
            
            {missedCriteria.length > 0 && (
              <div>
                <h6 className="text-danger d-flex align-items-center small fw-bold mt-1">
                  <FaTimesCircle className="me-1" size="0.8em" /> Missed Criteria
                </h6>
                <ListGroup variant="flush" className="border-top border-bottom">
                  {missedCriteria.map((criterion, index) => (
                    <ListGroup.Item key={index} className="py-1 px-2 d-flex justify-content-between align-items-center">
                      <div>
                        <span className="fw-medium small">{criterion.fieldName}</span>
                        <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                          {criterion.operator} {criterion.value}
                        </div>
                      </div>
                      <Badge 
                        bg={criterion.required ? 'danger' : 'secondary'} 
                        className="ms-1"
                        style={{ fontSize: '0.65rem' }}
                      >
                        {criterion.required ? 'Required' : `W: ${criterion.weight}`}
                      </Badge>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </div>
            )}
            
            {matchedCriteria.length === 0 && missedCriteria.length === 0 && (
              <div className="text-center text-muted p-2 small">
                <FaInfoCircle className="me-1" size="0.8em" />
                No criteria have been defined for this lender yet.
              </div>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

LenderMatchScore.propTypes = {
  lenderId: PropTypes.string.isRequired,
  lenderName: PropTypes.string.isRequired,
  score: PropTypes.number.isRequired,
  matchedCriteria: PropTypes.array,
  missedCriteria: PropTypes.array,
  expanded: PropTypes.bool,
  onToggleExpand: PropTypes.func.isRequired
};

export default LenderMatchScore; 